/**
 * The Read board's strip: the scan, who reads it, and the true score to count against.
 *
 * Three things are asked and each has one control. The reader is a menu because readers are
 * what the board compares: every one the pipeline knows is listed, and one this server has
 * not been given is listed too, greyed and saying why, so that what is missing is seen
 * rather than guessed at. A reader that is not ours to ship says so in its name.
 *
 * Under the strip, while a scan is read, the same line a run is followed by: the page being
 * read, how far through the scan that is, and the button that stops it.
 */

import { useRef } from "react";
import type { RecogniseProgress, ScoreReader } from "@app/shared-types";
import { ACTION, ACTION_LIVE, Divider, LABEL, SELECT, STRIP } from "./Chrome";
import { PAGES_ACCEPT } from "../lib/pictures";
import type { ScanProgress } from "../lib/scan-read";

/** A reader as the menu names it: what it is called, and what stands in the way of it. */
function named(reader: ScoreReader): string {
  const lab = reader.shipped ? "" : " · lab only";
  return `${reader.label}${lab}${reader.why === null ? "" : ` · ${reader.absent}`}`;
}

/** The true score: a file picker's offer of MusicXML, plain or zipped. */
const TRUTH_ACCEPT =
  ".musicxml,.xml,.mxl,application/vnd.recordare.musicxml+xml,application/vnd.recordare.musicxml,text/xml";

export interface ReadToolbarProps {
  readonly scan: File | null;
  /** Photos being made into the scan, by what they are called, or `null`. */
  readonly binding: string | null;
  /** Pages in the scan, once this browser has opened it. */
  readonly pages: number | null;
  readonly readers: readonly ScoreReader[];
  /** The reader chosen, by name. */
  readonly reader: string;
  readonly truth: File | null;
  readonly reading: boolean;
  /** A scan picked: a PDF, or photos or pictures of its pages. */
  onScan(files: readonly File[]): void;
  onReader(name: string): void;
  onTruth(file: File | null): void;
  onRead(): void;
}

export default function ReadToolbar({
  scan,
  binding,
  pages,
  readers,
  reader,
  truth,
  reading,
  onScan,
  onReader,
  onTruth,
  onRead,
}: ReadToolbarProps): JSX.Element {
  const scanInput = useRef<HTMLInputElement>(null);
  const truthInput = useRef<HTMLInputElement>(null);
  const chosen = readers.find((one) => one.name === reader);
  const ready = chosen !== undefined && chosen.why === null;

  return (
    <div className={STRIP}>
      <input
        ref={scanInput}
        type="file"
        accept={PAGES_ACCEPT}
        multiple
        className="hidden"
        onChange={(event) => {
          const files = [...(event.target.files ?? [])];
          // Emptied, so that opening the same file again is still a change.
          event.target.value = "";
          if (files.length > 0) onScan(files);
        }}
      />
      <button
        type="button"
        onClick={() => scanInput.current?.click()}
        className={`${ACTION} bg-shell-key`}
      >
        Open a PDF or photos…
      </button>
      {binding !== null ? (
        <>
          <span className="max-w-[16rem] truncate text-[11px] text-shell-lit" title={binding}>
            {binding}
          </span>
          <span className="font-mono text-[10px] text-shell-dim">making pages…</span>
        </>
      ) : scan === null ? null : (
        <>
          <span className="max-w-[16rem] truncate text-[11px] text-shell-lit" title={scan.name}>
            {scan.name}
          </span>
          {pages === null ? null : (
            <span className="font-mono text-[10px] text-shell-dim">
              {pages} {pages === 1 ? "page" : "pages"}
            </span>
          )}
        </>
      )}
      <Divider />

      <label className="flex items-center gap-2">
        <span className={LABEL}>Read by</span>
        <select
          value={reader}
          onChange={(event) => onReader(event.target.value)}
          className={SELECT}
          title={chosen === undefined ? undefined : (chosen.why ?? chosen.about)}
        >
          {readers.map((one) => (
            <option key={one.name} value={one.name} disabled={one.why !== null}>
              {named(one)}
            </option>
          ))}
        </select>
      </label>
      <button
        type="button"
        onClick={onRead}
        disabled={scan === null || binding !== null || reading || !ready}
        title={
          scan === null
            ? "Open a PDF or photos of printed music first"
            : chosen !== undefined && chosen.why !== null
              ? chosen.why
              : undefined
        }
        className={ACTION_LIVE}
      >
        {reading ? "Reading…" : "Read"}
      </button>

      <span className="ml-auto flex items-center gap-2">
        <span className={LABEL}>Count against</span>
        <input
          ref={truthInput}
          type="file"
          accept={TRUTH_ACCEPT}
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (file !== undefined) onTruth(file);
          }}
        />
        {truth === null ? (
          <button
            type="button"
            onClick={() => truthInput.current?.click()}
            title="The MusicXML the page was printed from, if there is one: every reading is then counted against it, note for note"
            className="inline-flex h-7 items-center rounded border border-dashed border-shell-seam px-2.5 text-[11px] text-shell-text hover:bg-shell-hot hover:text-shell-lit"
          >
            the true score…
          </button>
        ) : (
          <span className="inline-flex h-7 items-center gap-1 rounded bg-shell-key pl-2.5 text-[11px] text-shell-lit">
            <span className="max-w-[14rem] truncate" title={truth.name}>
              {truth.name}
            </span>
            <button
              type="button"
              aria-label={`Stop counting against ${truth.name}`}
              onClick={() => onTruth(null)}
              className="h-7 rounded px-2 text-shell-dim hover:bg-shell-hot hover:text-shell-lit"
            >
              ×
            </button>
          </span>
        )}
      </span>
    </div>
  );
}

/** How many pages are through, where the reader reads a page at a time and has said. */
function through(progress: RecogniseProgress): string {
  const { done, pages } = progress;
  return done === undefined || pages === undefined ? "" : `, ${done} of ${pages} pages done`;
}

/** How far through the scan a reading is, as a sentence. */
function saying(progress: ScanProgress): string {
  if (progress.stage === "uploading") return "Sending the scan…";
  if (progress.stage === "look") {
    return progress.page === undefined || progress.pages === undefined
      ? "Looking through the scan for its pages of music…"
      : `Looking at page ${progress.page} of ${progress.pages} for its music${through(progress)}…`;
  }
  if (progress.page !== undefined && progress.pages !== undefined) {
    return `Reading page ${progress.page} of ${progress.pages}…`;
  }
  return progress.pages === undefined
    ? "Opening the scan…"
    : `Reading the scan${through(progress)}…`;
}

/** The share of the scan already read, or `null` before the reader has counted its pages. */
function share(progress: ScanProgress): number | null {
  if (progress.stage === "uploading" || progress.pages === undefined || progress.pages < 1) {
    return null;
  }
  const { done, page, pages } = progress;
  if (done !== undefined) return done / pages;
  return progress.stage === "scan" && page !== undefined ? (page - 1) / pages : null;
}

export function ReadProgress({
  progress,
  onStop,
}: {
  readonly progress: ScanProgress;
  onStop(): void;
}): JSX.Element {
  const part = share(progress);
  return (
    <div
      role="status"
      className="flex shrink-0 items-center gap-3 border-b border-shell-edge bg-shell-bar px-2.5 py-1.5 text-[11px] text-shell-text"
    >
      <span className="min-w-0 truncate">{saying(progress)}</span>
      <span className="h-1 min-w-[4rem] flex-1 overflow-hidden rounded-full bg-shell-key">
        {/* Nothing is drawn before the pages are counted: a bar at a guess is a wrong bar. */}
        {part === null ? null : (
          <span
            className="block h-full rounded-full bg-shell-live transition-[width]"
            style={{ width: `${Math.max(part, 0.02) * 100}%` }}
          />
        )}
      </span>
      <button
        type="button"
        onClick={onStop}
        className="h-6 shrink-0 rounded border border-shell-seam px-2.5 text-[11px] text-shell-text hover:bg-shell-key"
      >
        Stop
      </button>
    </div>
  );
}
